"use client";

import * as X from "@/ui";
import Intercom from "@intercom/messenger-js-sdk";


/** keep disabled; it does not reappear properly on page change */
const StartStopIntercomOnPageChange = false as boolean;


export function useIntercom(showIntercom: boolean, user?: User) {
  /*
   * show/hide intercom on page change by forcing styles.
   */
  X.useEffect([showIntercom], () => {
    if (!getAvailableAppId()) {
      return;
    }

    if (showIntercom === false) {
      document.body.classList.add("X-Intercom-Hidden");

    } else {
      document.body.classList.remove("X-Intercom-Hidden");
    }
  });

  /*
   * this logic is also replicated in `@/providers/cookies`, but is needed here
   * in case of the page change and the consent was already granted/denied.
   */
  X.useEffect([showIntercom, user], () => {
    if (StartStopIntercomOnPageChange) {
      const appId = getAvailableAppId();

      if (appId === null) {
        return;
      }

      const cookieConsent = window.CookieConsent;
      const hasFunctionalityConsent = cookieConsent?.acceptedCategory("functionality");

      if (showIntercom && hasFunctionalityConsent) {
        startIntercom(user);
        return;
      }

      return () => {
        stopIntercom();
      };
    }
  });
}


export function updateIntercomConsent(consentGranted: boolean) {
  if (!consentGranted) {
    stopIntercom();
    return;
  }

  startIntercom();
}


function startIntercom(user?: User) {
  const appId = getAvailableAppId();

  if (!appId) {
    return;
  }

  const param = {
    app_id: appId,
    custom_launcher_selector: ".X-Open-Intercom",
    ...user,
  };

  if (typeof window.Intercom === "function") {
    window.Intercom("boot", param);
    return;
  }

  Intercom(param);
}


function stopIntercom() {
  if (isBrowserContext() && typeof window.Intercom === "function") {
    window.Intercom("shutdown");
  }
}


type User = {
  user_id?: string;
  email?: string;
  name?: string;
  created_at?: number;
};


function getAvailableAppId() {
  if (!isBrowserContext()) {
    return null;
  }

  return process.env.NEXT_PUBLIC_INTERCOM_APP_ID || null;
}


function isBrowserContext() {
  return typeof window !== "undefined";
}
